import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { CdkObserveContent, ObserversModule } from '@angular/cdk/observers';
import * as i0 from '@angular/core';
import { EventEmitter, inject, Injector, forwardRef, booleanAttribute, Input, Output, ViewChild, ContentChildren, Optional, ChangeDetectionStrategy, Component, NgModule } from '@angular/core';
import * as i2 from '@angular/forms';
import { Validators, NgControl, NG_VALUE_ACCESSOR, NG_VALIDATORS } from '@angular/forms';
import { NxErrorComponent } from '@aposin/ng-aquila/base';
import * as i4 from '@aposin/ng-aquila/icon';
import { NxIconModule } from '@aposin/ng-aquila/icon';
import { NxAbstractControl } from '@aposin/ng-aquila/shared';
import * as i1 from '@aposin/ng-aquila/utils';
import { randomString } from '@aposin/ng-aquila/utils';
import { Subject, asapScheduler } from 'rxjs';
import { startWith, observeOn, takeUntil } from 'rxjs/operators';
import * as i3 from '@angular/cdk/a11y';
import { CommonModule } from '@angular/common';
const _c0 = ["switcherLabelWrapper"];
const _c1 = ["input"];
const _c2 = ["*", [["nx-error"]]];
const _c3 = ["*", "nx-error"];
function NxSwitcherComponent_Conditional_9_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 8);
    i0.ɵɵprojection(1, 1);
    i0.ɵɵelementEnd();
  }
}
let nextId = 0;
class NxSwitcherComponent {
  set ariaDescribedBy(value) {
    this._ariaDescribedBy = value;
    this._syncDescribedByIds();
    this._cdr.markForCheck();
  }
  get ariaDescribedBy() {
    return this._ariaDescribedBy;
  }
  /** Sets the id of the switcher */
  set id(value) {
    this._id = value;
    this._cdr.markForCheck();
  }
  get id() {
    return this._id;
  }
  /** Specifies the placement of the label */
  set labelPosition(value) {
    this._labelPosition = value;
    this._cdr.markForCheck();
  }
  get labelPosition() {
    return this._labelPosition;
  }
  /** Sets the label text of the switcher */
  set name(value) {
    this._name = value;
    this._cdr.markForCheck();
  }
  get name() {
    return this._name;
  }
  /** Whether the switcher is checked (on) or unchecked (off) */
  set checked(value) {
    this._checked = value;
    this._cdr.markForCheck();
  }
  get checked() {
    return this._checked;
  }
  /** Whether the big switcher is used */
  set big(value) {
    const newValue = coerceBooleanProperty(value);
    this._big = newValue;
    this._cdr.markForCheck();
  }
  get big() {
    return this._big;
  }
  /** Sets the size of the label */
  set labelSize(value) {
    this._labelSize = value;
    this._cdr.markForCheck();
  }
  get labelSize() {
    return this._labelSize;
  }
  /** Whether the style for a dark background is used */
  set negative(value) {
    const newValue = coerceBooleanProperty(value);
    this._negative = newValue;
    this._cdr.markForCheck();
  }
  get negative() {
    return this._negative;
  }
  /** Whether the switcher is in the disabled state */
  set disabled(value) {
    const newValue = coerceBooleanProperty(value);
    this._disabled = newValue;
    this._cdr.markForCheck();
  }
  get disabled() {
    return this._disabled;
  }
  set required(value) {
    this._required = value;
  }
  get required() {
    return this._required ?? this.ngControl?.control?.hasValidator(Validators.requiredTrue) ?? false;
  }
  constructor(_cdr, _errorStateMatcher, _parentForm, _parentFormGroup, _focusMonitor) {
    this._cdr = _cdr;
    this._errorStateMatcher = _errorStateMatcher;
    this._parentForm = _parentForm;
    this._parentFormGroup = _parentFormGroup;
    this._focusMonitor = _focusMonitor;
    /** @docs-private */
    this.errorState = false;
    this.ariaLabel = null;
    this.ariaLabelledBy = null;
    this._ariaDescribedBy = null;
    this._id = `nx-switcher-${nextId++}-${randomString()}`;
    this._labelPosition = 'right';
    this._name = null;
    this._checked = false;
    this._big = false;
    this._labelSize = 'large';
    this._negative = false;
    this._disabled = false;
    /** An event is dispatched each time the switcher value is changed */
    this.checkedChange = new EventEmitter();
    this.onChangeCallback = _ => {};
    this.onTouchedCallback = () => {};
    this._destroyed = new Subject();
    this.ngControl = null;
    this.injector = inject(Injector);
    /** Sets switcher to readonly. */
    this.readonly = false;
  }
  validate(control) {
    return this.required && control.value !== true ? {
      required: true
    } : null;
  }
  ngOnInit() {
    this.ngControl = this.injector.get(NgControl, null);
  }
  ngAfterViewInit() {
    this._focusMonitor.monitor(this._nativeInput);
    this._errorChildren.changes.pipe(startWith(null), observeOn(asapScheduler), takeUntil(this._destroyed)).subscribe(() => {
      this._syncDescribedByIds();
      this._cdr.markForCheck();
    });
  }
  ngOnDestroy() {
    this._focusMonitor.stopMonitoring(this._nativeInput);
    this._destroyed.next();
    this._destroyed.complete();
  }
  /** Allows to toggle between the states */
  toggle(event) {
    event.stopPropagation();
    if (this.disabled || this.readonly) {
      event.preventDefault();
      return;
    }
    this.checked = !this.checked;
    this.onChangeCallback(this.checked);
    this.checkedChange.emit(this.checked);
    if (this.onTouchedCallback) {
      this.onTouchedCallback();
    }
  }
  writeValue(value) {
    if (value === null) {
      value = false;
    }
    if (value !== this.checked) {
      this.checked = value;
    }
  }
  registerOnChange(fn) {
    this.onChangeCallback = fn;
  }
  registerOnTouched(fn) {
    this.onTouchedCallback = fn;
  }
  ngDoCheck() {
    if (this.ngControl) {
      // We need to re-evaluate this on every change detection cycle, because there are some
      // error triggers that we can't subscribe to (e.g. parent form submissions). This means
      // that whatever logic is in here has to be super lean or we risk destroying the performance.
      this.updateErrorState();
      this._cdr.markForCheck();
    }
  }
  setDisabledState(isDisabled) {
    this.disabled = isDisabled;
  }
  /** @docs-private */
  touch() {
    this.onTouchedCallback();
  }
  /** @docs-private */
  updateErrorState() {
    const oldState = this.errorState;
    const parent = this._parentFormGroup || this._parentForm;
    const control = this.ngControl ? this.ngControl.control : null;
    const newState = this._errorStateMatcher.isErrorState(control, parent);
    if (newState !== oldState) {
      this.errorState = newState;
    }
  }
  /** @docs-private */
  get labelHasContent() {
    return !!this._switcherLabelWrapper.nativeElement.innerHTML.trim();
  }
  /**
   * Callback for when the content of the label has changed.
   * @docs-private
   */
  labelContentChanged() {
    this._cdr.detectChanges();
  }
  /** Forward focus from host to hidden input field */
  _forwardFocusToInput() {
    this._nativeInput.nativeElement.focus();
  }
  _syncDescribedByIds() {
    const errorChildren = this._errorChildren || [];
    this._ariaDescribedBy = [...errorChildren.map(error => error.id), this.ariaDescribedBy].join(' ');
  }
  setReadonly(value) {
    this.readonly = value;
    this._cdr.markForCheck();
  }
  static {
    this.ɵfac = function NxSwitcherComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NxSwitcherComponent)(i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i1.ErrorStateMatcher), i0.ɵɵdirectiveInject(i2.NgForm, 8), i0.ɵɵdirectiveInject(i2.FormGroupDirective, 8), i0.ɵɵdirectiveInject(i3.FocusMonitor));
    };
  }
  static {
    this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: NxSwitcherComponent,
      selectors: [["nx-switcher"]],
      contentQueries: function NxSwitcherComponent_ContentQueries(rf, ctx, dirIndex) {
        if (rf & 1) {
          i0.ɵɵcontentQuery(dirIndex, NxErrorComponent, 4);
        }
        if (rf & 2) {
          let _t;
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx._errorChildren = _t);
        }
      },
      viewQuery: function NxSwitcherComponent_Query(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵviewQuery(_c0, 7);
          i0.ɵɵviewQuery(_c1, 5);
        }
        if (rf & 2) {
          let _t;
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx._switcherLabelWrapper = _t.first);
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx._nativeInput = _t.first);
        }
      },
      hostVars: 21,
      hostBindings: function NxSwitcherComponent_HostBindings(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵlistener("focus", function NxSwitcherComponent_focus_HostBindingHandler() {
            return ctx._forwardFocusToInput();
          });
        }
        if (rf & 2) {
          i0.ɵɵattribute("id", null);
          i0.ɵɵclassProp("is-negative", ctx.negative)("is-checked", ctx.checked)("is-big", ctx.big)("check-icon-small", !ctx.big)("nx-switcher--small-label", ctx.labelSize === "small")("nx-switcher--large-label", ctx.labelSize === "large")("is-disabled", ctx.disabled)("is-swapped", ctx.labelPosition === "left")("has-error", ctx.errorState)("is-readonly", ctx.readonly);
        }
      },
      inputs: {
        ariaLabel: "ariaLabel",
        ariaLabelledBy: "ariaLabelledBy",
        ariaDescribedBy: "ariaDescribedBy",
        id: "id",
        labelPosition: "labelPosition",
        name: "name",
        checked: "checked",
        big: "big",
        labelSize: "labelSize",
        negative: "negative",
        disabled: "disabled",
        required: [2, "required", "required", booleanAttribute],
        readonly: [2, "readonly", "readonly", booleanAttribute]
      },
      outputs: {
        checkedChange: "checkedChange"
      },
      features: [i0.ɵɵProvidersFeature([{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => NxSwitcherComponent),
        multi: true
      }, {
        provide: NG_VALIDATORS,
        useExisting: forwardRef(() => NxSwitcherComponent),
        multi: true
      }, {
        provide: NxAbstractControl,
        useExisting: forwardRef(() => NxSwitcherComponent)
      }])],
      ngContentSelectors: _c3,
      decls: 10,
      vars: 16,
      consts: [["input", ""], ["switcherLabelWrapper", ""], ["type", "checkbox", "role", "switch", 1, "nx-switcher__input", 3, "click", "blur", "id", "disabled", "checked"], [1, "nx-switcher__control"], [1, "nx-switcher__toggle"], [1, "nx-switcher__dot"], ["name", "check", "aria-hidden", "true"], [1, "nx-switcher__label-text", 3, "cdkObserveContent"], [1, "nx-switcher-error"]],
      template: function NxSwitcherComponent_Template(rf, ctx) {
        if (rf & 1) {
          const _r1 = i0.ɵɵgetCurrentView();
          i0.ɵɵprojectionDef(_c2);
          i0.ɵɵelementStart(0, "input", 2, 0);
          i0.ɵɵlistener("click", function NxSwitcherComponent_Template_input_click_0_listener($event) {
            i0.ɵɵrestoreView(_r1);
            return i0.ɵɵresetView(ctx.toggle($event));
          })("blur", function NxSwitcherComponent_Template_input_blur_0_listener() {
            i0.ɵɵrestoreView(_r1);
            return i0.ɵɵresetView(ctx.touch());
          });
          i0.ɵɵelementEnd();
          i0.ɵɵelementStart(2, "label", 3)(3, "div", 4)(4, "div", 5);
          i0.ɵɵelement(5, "nx-icon", 6);
          i0.ɵɵelementEnd()();
          i0.ɵɵelementStart(6, "div", 7, 1);
          i0.ɵɵlistener("cdkObserveContent", function NxSwitcherComponent_Template_div_cdkObserveContent_6_listener() {
            i0.ɵɵrestoreView(_r1);
            return i0.ɵɵresetView(ctx.labelContentChanged());
          });
          i0.ɵɵprojection(8);
          i0.ɵɵelementEnd()();
          i0.ɵɵtemplate(9, NxSwitcherComponent_Conditional_9_Template, 2, 0, "div", 8);
        }
        if (rf & 2) {
          i0.ɵɵproperty("id", ctx.id)("disabled", ctx.disabled)("checked", ctx.checked);
          i0.ɵɵattribute("name", ctx.name)("aria-checked", ctx.checked)("aria-labelledby", ctx.ariaLabelledBy)("aria-describedby", ctx.ariaDescribedBy)("aria-label", ctx.ariaLabel)("aria-invalid", ctx.errorState)("aria-required", ctx.required)("aria-disabled", ctx.readonly || null)("required", ctx.required || null);
          i0.ɵɵadvance(2);
          i0.ɵɵclassProp("has-label", ctx.labelHasContent);
          i0.ɵɵattribute("for", ctx.id);
          i0.ɵɵadvance(7);
          i0.ɵɵconditional(ctx.errorState ? 9 : -1);
        }
      },
      dependencies: [NxIconModule, i4.NxIconComponent, CdkObserveContent],
      styles: ["[_nghost-%COMP%]{position:relative}.is-swapped[_nghost-%COMP%]   .nx-switcher__label[_ngcontent-%COMP%]{flex-direction:row-reverse;justify-content:flex-end}.is-swapped[_nghost-%COMP%]   .nx-switcher__label.has-label[_ngcontent-%COMP%]   .nx-switcher__toggle[_ngcontent-%COMP%]{margin-left:12px}[dir=rtl]   .is-swapped[_nghost-%COMP%]   .nx-switcher__label.has-label[_ngcontent-%COMP%]   .nx-switcher__toggle[_ngcontent-%COMP%]{margin-right:12px;margin-left:initial}.nx-switcher__input[_ngcontent-%COMP%]{opacity:0;position:absolute}.nx-switcher__control[_ngcontent-%COMP%]{cursor:pointer;display:flex;position:relative}.is-swapped[_nghost-%COMP%]   .nx-switcher__control[_ngcontent-%COMP%]{flex-direction:row-reverse;justify-content:flex-end}.is-swapped[_nghost-%COMP%]   .nx-switcher__control[_ngcontent-%COMP%]   .nx-switcher__label-text[_ngcontent-%COMP%]{margin-right:12px}.nx-switcher__label-text[_ngcontent-%COMP%]{font-size:var(--switcher-large-font-size);line-height:var(--switcher-large-line-height);font-weight:var(--switcher-large-font-weight);letter-spacing:var(--switcher-large-letter-spacing)}.nx-switcher__toggle[_ngcontent-%COMP%]{position:relative;display:inline-block;width:48px;min-width:48px;height:24px;background-color:var(--switcher-unchecked-background-color);border-radius:16px;transition:background-color .2s ease}[_nghost-%COMP%]:not(.is-swapped)   .has-label[_ngcontent-%COMP%]   .nx-switcher__toggle[_ngcontent-%COMP%]{margin-right:12px}[dir=rtl]   [_nghost-%COMP%]:not(.is-swapped)   .has-label[_ngcontent-%COMP%]   .nx-switcher__toggle[_ngcontent-%COMP%]{margin-right:initial;margin-left:12px}.nx-switcher__dot[_ngcontent-%COMP%]{position:absolute;height:20px;width:20px;left:2px;bottom:2px;background-color:var(--switcher-dot-background-color);border-radius:16px;transform:translate(0);transition:transform .2s ease}[dir=rtl][_nghost-%COMP%]   .nx-switcher__dot[_ngcontent-%COMP%], [dir=rtl]   [_nghost-%COMP%]   .nx-switcher__dot[_ngcontent-%COMP%]{right:2px;left:auto}.is-checked[_nghost-%COMP%]   .nx-switcher__dot[_ngcontent-%COMP%]{transform:translate(24px)}[dir=rtl]   .is-checked[_nghost-%COMP%]   .nx-switcher__dot[_ngcontent-%COMP%]{transform:translate(-24px)}.is-checked[_nghost-%COMP%]   .nx-switcher__toggle[_ngcontent-%COMP%]{background-color:var(--switcher-checked-background-color)}.is-checked[_nghost-%COMP%]   nx-icon[_ngcontent-%COMP%]{opacity:1;transition:opacity .2s ease}.is-checked.is-negative[_nghost-%COMP%]   .nx-switcher__toggle[_ngcontent-%COMP%]{background-color:var(--negative-dimmed)}.check-icon-small[_nghost-%COMP%]{font-size:16px}nx-icon[_ngcontent-%COMP%]{position:absolute;top:2px;left:2px;opacity:0;color:var(--switcher-checked-icon-color)}.is-big[_nghost-%COMP%]   .nx-switcher__toggle[_ngcontent-%COMP%]{width:56px;min-width:56px;height:32px;border-radius:32px}.is-big[_nghost-%COMP%]   .nx-switcher__dot[_ngcontent-%COMP%]{height:28px;width:28px}.is-big[_nghost-%COMP%]   nx-icon[_ngcontent-%COMP%]{font-size:24px}[dir=rtl]   .is-big[_nghost-%COMP%]   nx-icon[_ngcontent-%COMP%]{right:2px;left:auto}.is-big.nx-switcher--large-label[_nghost-%COMP%]   .has-label[_ngcontent-%COMP%]   .nx-switcher__label-text[_ngcontent-%COMP%]{margin-top:var(--switcher-big-large-label-text-margin-top)}.is-big.nx-switcher--large-label[_nghost-%COMP%]   .has-label[_ngcontent-%COMP%]   .nx-switcher__toggle[_ngcontent-%COMP%]{margin-top:var(--switcher-big-large-label-control-margin-top)}.is-big.nx-switcher--small-label[_nghost-%COMP%]   .has-label[_ngcontent-%COMP%]   .nx-switcher__label-text[_ngcontent-%COMP%]{margin-top:var(--switcher-big-small-label-text-margin-top)}.is-big.nx-switcher--small-label[_nghost-%COMP%]   .has-label[_ngcontent-%COMP%]   .nx-switcher__toggle[_ngcontent-%COMP%]{margin-top:var(--switcher-big-small-label-control-margin-top)}.nx-switcher--large-label[_nghost-%COMP%]   .has-label[_ngcontent-%COMP%]   .nx-switcher__label-text[_ngcontent-%COMP%]{margin-top:var(--switcher-large-label-text-margin-top)}.nx-switcher--large-label[_nghost-%COMP%]   .has-label[_ngcontent-%COMP%]   .nx-switcher__toggle[_ngcontent-%COMP%]{margin-top:var(--switcher-large-label-control-margin-top)}.nx-switcher--small-label[_nghost-%COMP%]   .nx-switcher__label-text[_ngcontent-%COMP%]{font-size:var(--switcher-small-font-size);line-height:var(--switcher-small-line-height);font-weight:var(--switcher-small-font-weight);letter-spacing:var(--switcher-small-letter-spacing)}.nx-switcher--small-label[_nghost-%COMP%]   .has-label[_ngcontent-%COMP%]   .nx-switcher__label-text[_ngcontent-%COMP%]{margin-top:var(--switcher-small-label-text-margin-top)}.nx-switcher--small-label[_nghost-%COMP%]   .has-label[_ngcontent-%COMP%]   .nx-switcher__toggle[_ngcontent-%COMP%]{margin-top:var(--switcher-small-label-control-margin-top)}.is-negative[_nghost-%COMP%]   .nx-switcher__control[_ngcontent-%COMP%]{color:var(--negative)}.is-negative[_nghost-%COMP%]   .nx-switcher__dot[_ngcontent-%COMP%]{background-color:var(--negative)}.is-negative[_nghost-%COMP%]   .nx-switcher__toggle[_ngcontent-%COMP%]{background-color:var(--negative-dimmed)}.is-negative[_nghost-%COMP%]   nx-icon[_ngcontent-%COMP%]{color:var(--negative-background)}[_nghost-%COMP%]   .nx-switcher__input.cdk-keyboard-focused[_ngcontent-%COMP%] + .nx-switcher__control[_ngcontent-%COMP%]   .nx-switcher__toggle[_ngcontent-%COMP%]{box-shadow:var(--focus-box-shadow)}@media screen and (forced-colors: active),(forced-colors: active){[_nghost-%COMP%]   .nx-switcher__input.cdk-keyboard-focused[_ngcontent-%COMP%] + .nx-switcher__control[_ngcontent-%COMP%]   .nx-switcher__toggle[_ngcontent-%COMP%]{box-shadow:0 0 0 2px background,0 0 0 6px CanvasText;outline:4px solid CanvasText;outline-offset:2px}}.is-disabled[_nghost-%COMP%]   .nx-switcher__toggle[_ngcontent-%COMP%]{background-color:var(--switcher-disabled-background-color)}.is-disabled[_nghost-%COMP%]   .nx-switcher__dot[_ngcontent-%COMP%]{background-color:var(--switcher-disabled-dot-background-color)}.is-disabled[_nghost-%COMP%]   .nx-switcher__control[_ngcontent-%COMP%]{cursor:not-allowed;color:var(--switcher-disabled-text-color)}.is-disabled[_nghost-%COMP%]   nx-icon[_ngcontent-%COMP%]{color:var(--switcher-disabled-icon-color)}.is-readonly[_nghost-%COMP%]   .nx-switcher__toggle[_ngcontent-%COMP%]{background-color:var(--switcher-readonly-background-color);border:1px solid var(--switcher-readonly-border-color)}.is-readonly[_nghost-%COMP%]   .nx-switcher__dot[_ngcontent-%COMP%]{background-color:var(--switcher-readonly-dot-background-color);left:1px;bottom:1px}.is-readonly[_nghost-%COMP%]   .nx-switcher__dot[_ngcontent-%COMP%]   nx-icon[_ngcontent-%COMP%]{color:var(--switcher-readonly-icon-color)}.is-readonly[_nghost-%COMP%]   .nx-switcher__control[_ngcontent-%COMP%]{cursor:not-allowed;color:var(--switcher-readonly-text-color)}.has-error[_nghost-%COMP%]   .nx-switcher__toggle[_ngcontent-%COMP%]{background-color:var(--danger)}.has-error[_nghost-%COMP%]   nx-icon[_ngcontent-%COMP%]{color:var(--danger)}@media screen and (forced-colors: active){[_nghost-%COMP%]   .nx-switcher__control[_ngcontent-%COMP%], .is-negative[_nghost-%COMP%]   .nx-switcher__control[_ngcontent-%COMP%]{color:buttonText}[_nghost-%COMP%]   .nx-switcher__toggle[_ngcontent-%COMP%], .is-negative[_nghost-%COMP%]   .nx-switcher__toggle[_ngcontent-%COMP%]{forced-color-adjust:none;color:highlightText;background-color:highlight}[_nghost-%COMP%]:not(.is-checked)   .nx-switcher__toggle[_ngcontent-%COMP%], .is-negative[_nghost-%COMP%]:not(.is-checked)   .nx-switcher__toggle[_ngcontent-%COMP%]{background-color:buttonFace;border:2px solid buttonText;color:buttonText}[_nghost-%COMP%]:not(.is-checked)   .nx-switcher__dot[_ngcontent-%COMP%], .is-negative[_nghost-%COMP%]:not(.is-checked)   .nx-switcher__dot[_ngcontent-%COMP%]{left:0;bottom:0;box-shadow:0 0 0 2px buttonText;background-color:buttonText}.is-checked[_nghost-%COMP%]   .nx-switcher__toggle[_ngcontent-%COMP%], .is-negative.is-checked[_nghost-%COMP%]   .nx-switcher__toggle[_ngcontent-%COMP%]{background-color:highlight}.is-checked[_nghost-%COMP%]   .nx-switcher__dot[_ngcontent-%COMP%], .is-negative.is-checked[_nghost-%COMP%]   .nx-switcher__dot[_ngcontent-%COMP%]{background-color:highlightText;color:buttonText}.is-checked[_nghost-%COMP%]   .nx-switcher__dot[_ngcontent-%COMP%]   nx-icon[_ngcontent-%COMP%], .is-negative.is-checked[_nghost-%COMP%]   .nx-switcher__dot[_ngcontent-%COMP%]   nx-icon[_ngcontent-%COMP%]{color:highlight}.is-disabled[_nghost-%COMP%]   .nx-switcher__control[_ngcontent-%COMP%], .is-negative.is-disabled[_nghost-%COMP%]   .nx-switcher__control[_ngcontent-%COMP%]{color:GrayText}.is-disabled[_nghost-%COMP%]   .nx-switcher__toggle[_ngcontent-%COMP%], .is-negative.is-disabled[_nghost-%COMP%]   .nx-switcher__toggle[_ngcontent-%COMP%]{color:GrayText}.is-disabled[_nghost-%COMP%]   .nx-switcher__dot[_ngcontent-%COMP%], .is-negative.is-disabled[_nghost-%COMP%]   .nx-switcher__dot[_ngcontent-%COMP%]{opacity:1;background-color:GrayText}.is-disabled[_nghost-%COMP%]:not(.is-checked)   .nx-switcher__toggle[_ngcontent-%COMP%], .is-negative.is-disabled[_nghost-%COMP%]:not(.is-checked)   .nx-switcher__toggle[_ngcontent-%COMP%]{border:2px solid GrayText}.is-disabled[_nghost-%COMP%]:not(.is-checked)   .nx-switcher__dot[_ngcontent-%COMP%], .is-negative.is-disabled[_nghost-%COMP%]:not(.is-checked)   .nx-switcher__dot[_ngcontent-%COMP%]{box-shadow:0 0 0 2px GrayText}.is-disabled.is-checked[_nghost-%COMP%]   .nx-switcher__toggle[_ngcontent-%COMP%], .is-negative.is-disabled.is-checked[_nghost-%COMP%]   .nx-switcher__toggle[_ngcontent-%COMP%]{background-color:GrayText}.is-disabled.is-checked[_nghost-%COMP%]   .nx-switcher__dot[_ngcontent-%COMP%], .is-negative.is-disabled.is-checked[_nghost-%COMP%]   .nx-switcher__dot[_ngcontent-%COMP%]{background-color:Canvas}.is-disabled.is-checked[_nghost-%COMP%]   .nx-switcher__dot[_ngcontent-%COMP%]   nx-icon[_ngcontent-%COMP%], .is-negative.is-disabled.is-checked[_nghost-%COMP%]   .nx-switcher__dot[_ngcontent-%COMP%]   nx-icon[_ngcontent-%COMP%]{color:GrayText}}.nx-switcher-error[_ngcontent-%COMP%]{margin-top:var(--switcher-stack)}"],
      changeDetection: 0
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NxSwitcherComponent, [{
    type: Component,
    args: [{
      selector: 'nx-switcher',
      changeDetection: ChangeDetectionStrategy.OnPush,
      host: {
        /* the host id should be set to null, otherwise nx-switcher and its <input> get the same id
        and on label click the input click is not triggered (double id problem) */
        '[attr.id]': 'null',
        '[class.is-negative]': 'negative',
        '[class.is-checked]': 'checked',
        '[class.is-big]': 'big',
        '[class.check-icon-small]': '!big',
        '[class.nx-switcher--small-label]': 'labelSize === "small"',
        '[class.nx-switcher--large-label]': 'labelSize === "large"',
        '[class.is-disabled]': 'disabled',
        '[class.is-swapped]': 'labelPosition === "left"',
        '[class.has-error]': 'errorState',
        '[class.is-readonly]': 'readonly',
        '(focus)': '_forwardFocusToInput()'
      },
      imports: [NxIconModule, CdkObserveContent],
      providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => NxSwitcherComponent),
        multi: true
      }, {
        provide: NG_VALIDATORS,
        useExisting: forwardRef(() => NxSwitcherComponent),
        multi: true
      }, {
        provide: NxAbstractControl,
        useExisting: forwardRef(() => NxSwitcherComponent)
      }],
      template: "<input\n    #input\n    type=\"checkbox\"\n    [id]=\"id\"\n    [attr.name]=\"name\"\n    [disabled]=\"disabled\"\n    [checked]=\"checked\"\n    (click)=\"toggle($event)\"\n    (blur)=\"touch()\"\n    role=\"switch\"\n    [attr.aria-checked]=\"checked\"\n    [attr.aria-labelledby]=\"ariaLabelledBy\"\n    [attr.aria-describedby]=\"ariaDescribedBy\"\n    [attr.aria-label]=\"ariaLabel\"\n    [attr.aria-invalid]=\"errorState\"\n    [attr.aria-required]=\"required\"\n    [attr.aria-disabled]=\"readonly || null\"\n    [attr.required]=\"required || null\"\n    class=\"nx-switcher__input\"\n/>\n<label [attr.for]=\"id\" class=\"nx-switcher__control\" [class.has-label]=\"labelHasContent\">\n    <div class=\"nx-switcher__toggle\">\n        <div class=\"nx-switcher__dot\">\n            <nx-icon name=\"check\" aria-hidden=\"true\"></nx-icon>\n        </div>\n    </div>\n    <div class=\"nx-switcher__label-text\" #switcherLabelWrapper (cdkObserveContent)=\"labelContentChanged()\">\n        <ng-content></ng-content>\n    </div>\n</label>\n\n@if (errorState) {\n    <div class=\"nx-switcher-error\">\n        <ng-content select=\"nx-error\"></ng-content>\n    </div>\n}\n",
      styles: [":host{position:relative}:host.is-swapped .nx-switcher__label{flex-direction:row-reverse;justify-content:flex-end}:host.is-swapped .nx-switcher__label.has-label .nx-switcher__toggle{margin-left:12px}[dir=rtl] :host.is-swapped .nx-switcher__label.has-label .nx-switcher__toggle{margin-right:12px;margin-left:initial}.nx-switcher__input{opacity:0;position:absolute}.nx-switcher__control{cursor:pointer;display:flex;position:relative}:host(.is-swapped) .nx-switcher__control{flex-direction:row-reverse;justify-content:flex-end}:host(.is-swapped) .nx-switcher__control .nx-switcher__label-text{margin-right:12px}.nx-switcher__label-text{font-size:var(--switcher-large-font-size);line-height:var(--switcher-large-line-height);font-weight:var(--switcher-large-font-weight);letter-spacing:var(--switcher-large-letter-spacing)}.nx-switcher__toggle{position:relative;display:inline-block;width:48px;min-width:48px;height:24px;background-color:var(--switcher-unchecked-background-color);border-radius:16px;transition:background-color .2s ease}:host(:not(.is-swapped)) .has-label .nx-switcher__toggle{margin-right:12px}[dir=rtl] :host(:not(.is-swapped)) .has-label .nx-switcher__toggle{margin-right:initial;margin-left:12px}.nx-switcher__dot{position:absolute;height:20px;width:20px;left:2px;bottom:2px;background-color:var(--switcher-dot-background-color);border-radius:16px;transform:translate(0);transition:transform .2s ease}:host-context([dir=rtl]) .nx-switcher__dot{right:2px;left:auto}:host(.is-checked) .nx-switcher__dot{transform:translate(24px)}[dir=rtl] :host(.is-checked) .nx-switcher__dot{transform:translate(-24px)}:host(.is-checked) .nx-switcher__toggle{background-color:var(--switcher-checked-background-color)}:host(.is-checked) nx-icon{opacity:1;transition:opacity .2s ease}:host(.is-checked.is-negative) .nx-switcher__toggle{background-color:var(--negative-dimmed)}:host(.check-icon-small){font-size:16px}nx-icon{position:absolute;top:2px;left:2px;opacity:0;color:var(--switcher-checked-icon-color)}:host(.is-big) .nx-switcher__toggle{width:56px;min-width:56px;height:32px;border-radius:32px}:host(.is-big) .nx-switcher__dot{height:28px;width:28px}:host(.is-big) nx-icon{font-size:24px}[dir=rtl] :host(.is-big) nx-icon{right:2px;left:auto}:host(.is-big).nx-switcher--large-label .has-label .nx-switcher__label-text{margin-top:var(--switcher-big-large-label-text-margin-top)}:host(.is-big).nx-switcher--large-label .has-label .nx-switcher__toggle{margin-top:var(--switcher-big-large-label-control-margin-top)}:host(.is-big).nx-switcher--small-label .has-label .nx-switcher__label-text{margin-top:var(--switcher-big-small-label-text-margin-top)}:host(.is-big).nx-switcher--small-label .has-label .nx-switcher__toggle{margin-top:var(--switcher-big-small-label-control-margin-top)}:host(.nx-switcher--large-label) .has-label .nx-switcher__label-text{margin-top:var(--switcher-large-label-text-margin-top)}:host(.nx-switcher--large-label) .has-label .nx-switcher__toggle{margin-top:var(--switcher-large-label-control-margin-top)}:host(.nx-switcher--small-label) .nx-switcher__label-text{font-size:var(--switcher-small-font-size);line-height:var(--switcher-small-line-height);font-weight:var(--switcher-small-font-weight);letter-spacing:var(--switcher-small-letter-spacing)}:host(.nx-switcher--small-label) .has-label .nx-switcher__label-text{margin-top:var(--switcher-small-label-text-margin-top)}:host(.nx-switcher--small-label) .has-label .nx-switcher__toggle{margin-top:var(--switcher-small-label-control-margin-top)}:host(.is-negative) .nx-switcher__control{color:var(--negative)}:host(.is-negative) .nx-switcher__dot{background-color:var(--negative)}:host(.is-negative) .nx-switcher__toggle{background-color:var(--negative-dimmed)}:host(.is-negative) nx-icon{color:var(--negative-background)}:host .nx-switcher__input.cdk-keyboard-focused+.nx-switcher__control .nx-switcher__toggle{box-shadow:var(--focus-box-shadow)}@media screen and (forced-colors: active),(forced-colors: active){:host .nx-switcher__input.cdk-keyboard-focused+.nx-switcher__control .nx-switcher__toggle{box-shadow:0 0 0 2px background,0 0 0 6px CanvasText;outline:4px solid CanvasText;outline-offset:2px}}:host(.is-disabled) .nx-switcher__toggle{background-color:var(--switcher-disabled-background-color)}:host(.is-disabled) .nx-switcher__dot{background-color:var(--switcher-disabled-dot-background-color)}:host(.is-disabled) .nx-switcher__control{cursor:not-allowed;color:var(--switcher-disabled-text-color)}:host(.is-disabled) nx-icon{color:var(--switcher-disabled-icon-color)}:host(.is-readonly) .nx-switcher__toggle{background-color:var(--switcher-readonly-background-color);border:1px solid var(--switcher-readonly-border-color)}:host(.is-readonly) .nx-switcher__dot{background-color:var(--switcher-readonly-dot-background-color);left:1px;bottom:1px}:host(.is-readonly) .nx-switcher__dot nx-icon{color:var(--switcher-readonly-icon-color)}:host(.is-readonly) .nx-switcher__control{cursor:not-allowed;color:var(--switcher-readonly-text-color)}:host(.has-error) .nx-switcher__toggle{background-color:var(--danger)}:host(.has-error) nx-icon{color:var(--danger)}@media screen and (forced-colors: active){:host .nx-switcher__control,:host(.is-negative) .nx-switcher__control{color:buttonText}:host .nx-switcher__toggle,:host(.is-negative) .nx-switcher__toggle{forced-color-adjust:none;color:highlightText;background-color:highlight}:host:not(.is-checked) .nx-switcher__toggle,:host(.is-negative):not(.is-checked) .nx-switcher__toggle{background-color:buttonFace;border:2px solid buttonText;color:buttonText}:host:not(.is-checked) .nx-switcher__dot,:host(.is-negative):not(.is-checked) .nx-switcher__dot{left:0;bottom:0;box-shadow:0 0 0 2px buttonText;background-color:buttonText}:host.is-checked .nx-switcher__toggle,:host(.is-negative).is-checked .nx-switcher__toggle{background-color:highlight}:host.is-checked .nx-switcher__dot,:host(.is-negative).is-checked .nx-switcher__dot{background-color:highlightText;color:buttonText}:host.is-checked .nx-switcher__dot nx-icon,:host(.is-negative).is-checked .nx-switcher__dot nx-icon{color:highlight}:host.is-disabled .nx-switcher__control,:host(.is-negative).is-disabled .nx-switcher__control{color:GrayText}:host.is-disabled .nx-switcher__toggle,:host(.is-negative).is-disabled .nx-switcher__toggle{color:GrayText}:host.is-disabled .nx-switcher__dot,:host(.is-negative).is-disabled .nx-switcher__dot{opacity:1;background-color:GrayText}:host.is-disabled:not(.is-checked) .nx-switcher__toggle,:host(.is-negative).is-disabled:not(.is-checked) .nx-switcher__toggle{border:2px solid GrayText}:host.is-disabled:not(.is-checked) .nx-switcher__dot,:host(.is-negative).is-disabled:not(.is-checked) .nx-switcher__dot{box-shadow:0 0 0 2px GrayText}:host.is-disabled.is-checked .nx-switcher__toggle,:host(.is-negative).is-disabled.is-checked .nx-switcher__toggle{background-color:GrayText}:host.is-disabled.is-checked .nx-switcher__dot,:host(.is-negative).is-disabled.is-checked .nx-switcher__dot{background-color:Canvas}:host.is-disabled.is-checked .nx-switcher__dot nx-icon,:host(.is-negative).is-disabled.is-checked .nx-switcher__dot nx-icon{color:GrayText}}.nx-switcher-error{margin-top:var(--switcher-stack)}\n"]
    }]
  }], () => [{
    type: i0.ChangeDetectorRef
  }, {
    type: i1.ErrorStateMatcher
  }, {
    type: i2.NgForm,
    decorators: [{
      type: Optional
    }]
  }, {
    type: i2.FormGroupDirective,
    decorators: [{
      type: Optional
    }]
  }, {
    type: i3.FocusMonitor
  }], {
    _switcherLabelWrapper: [{
      type: ViewChild,
      args: ['switcherLabelWrapper', {
        static: true
      }]
    }],
    _errorChildren: [{
      type: ContentChildren,
      args: [NxErrorComponent]
    }],
    _nativeInput: [{
      type: ViewChild,
      args: ['input']
    }],
    ariaLabel: [{
      type: Input
    }],
    ariaLabelledBy: [{
      type: Input
    }],
    ariaDescribedBy: [{
      type: Input
    }],
    id: [{
      type: Input
    }],
    labelPosition: [{
      type: Input
    }],
    name: [{
      type: Input
    }],
    checked: [{
      type: Input
    }],
    big: [{
      type: Input
    }],
    labelSize: [{
      type: Input
    }],
    negative: [{
      type: Input
    }],
    disabled: [{
      type: Input
    }],
    checkedChange: [{
      type: Output
    }],
    required: [{
      type: Input,
      args: [{
        transform: booleanAttribute
      }]
    }],
    readonly: [{
      type: Input,
      args: [{
        transform: booleanAttribute
      }]
    }]
  });
})();
class NxSwitcherModule {
  static {
    this.ɵfac = function NxSwitcherModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NxSwitcherModule)();
    };
  }
  static {
    this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: NxSwitcherModule,
      imports: [CommonModule, NxIconModule, ObserversModule, NxSwitcherComponent],
      exports: [NxSwitcherComponent]
    });
  }
  static {
    this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      imports: [CommonModule, NxIconModule, ObserversModule, NxSwitcherComponent]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NxSwitcherModule, [{
    type: NgModule,
    args: [{
      exports: [NxSwitcherComponent],
      imports: [CommonModule, NxIconModule, ObserversModule, NxSwitcherComponent]
    }]
  }], null, null);
})();

/**
 * Generated bundle index. Do not edit.
 */

export { NxSwitcherComponent, NxSwitcherModule };
